import { IApiClient, getApiClient } from "@/api/ApiClient/ApiClient";
import { BusinessDonor, PrivateDonor } from "@/models/APIModels/Donor";
import logger, { LoggingSource } from "@/shared/Logger";
import IResponseModel from "./ResponseModel";
import { UserSecurityDetail } from "@/models/APIModels/UserSecurityDetail";
import LoggerMasker from "@/shared/Logger/LoggerMasker";

export default class DonorApi {
  private readonly _apiClient: IApiClient;
  private readonly URL: string = "/donor";

  constructor(apiClient: IApiClient = getApiClient()) {
    this._apiClient = apiClient;
  }

  async insert(donor: PrivateDonor | BusinessDonor): Promise<
    IResponseModel<{
      accessToken: string;
      refreshToken: string;
      user: UserSecurityDetail;
    }>
  > {
    try {
      logger.info("insert...", LoggingSource.UIDonor);

      logger.debug(
        `Parameters => Donor: ${JSON.stringify(
          LoggerMasker.maskValues(donor)
        )}`,
        LoggingSource.UIDonor
      );

      logger.timeStart("insert call", LoggingSource.UIDonor);
      const result = await this._apiClient.post<
        PrivateDonor | BusinessDonor,
        {
          accessToken: string;
          refreshToken: string;
          user: UserSecurityDetail;
        }
      >(this.URL, donor);
      logger.timeEnd("insert call", LoggingSource.UIDonor);

      logger.debug(
        `insert response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonor
      );
      return result;
    } catch (error) {
      logger.timeEnd("insert call", LoggingSource.UIDonor);
      logger.error(JSON.stringify(error), LoggingSource.UIDonor);
      throw error;
    } finally {
      logger.info("...insert", LoggingSource.UIDonor);
    }
  }

  async getAll(
    queryString?: string
  ): Promise<IResponseModel<(PrivateDonor | BusinessDonor)[]>> {
    try {
      logger.info("getAll...", LoggingSource.UIDonor);

      logger.debug(
        `Parameters => QueryString: ${queryString}`,
        LoggingSource.UIDonor
      );

      logger.timeStart("getAll call", LoggingSource.UIDonor);
      const result = await this._apiClient.get<
        (PrivateDonor | BusinessDonor)[]
      >(this.URL + `?${queryString}`);
      logger.timeEnd("getAll call", LoggingSource.UIDonor);
      logger.debug(
        `getAll Response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonor
      );
      return result;
    } catch (error) {
      logger.timeEnd("getAll call", LoggingSource.UIDonor);
      logger.error(JSON.stringify(error), LoggingSource.UIDonor);
      throw error;
    } finally {
      logger.info("...getAll", LoggingSource.UIDonor);
    }
  }

  async update(
    donor: PrivateDonor | BusinessDonor,
    userReference: string
  ): Promise<IResponseModel<PrivateDonor | BusinessDonor>> {
    try {
      logger.info("update...", LoggingSource.UIDonor);

      logger.debug(
        `Parameters => Donor: ${JSON.stringify(donor)}`,
        LoggingSource.UIDonor
      );

      logger.timeStart("update call", LoggingSource.UIDonor);
      const result = await this._apiClient.put<
        PrivateDonor | BusinessDonor,
        PrivateDonor | BusinessDonor
      >(`${this.URL}/${userReference}`, donor);

      logger.timeEnd("update call", LoggingSource.UIDonor);

      logger.debug(
        `update response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonor
      );
      return result;
    } catch (error) {
      logger.timeEnd("update call", LoggingSource.UIDonor);
      logger.error(JSON.stringify(error), LoggingSource.UIDonor);
      throw error;
    } finally {
      logger.info("...update", LoggingSource.UIDonor);
    }
  }

  async activateDonations(
    donor: PrivateDonor | BusinessDonor,
    userReference: string
  ): Promise<IResponseModel<PrivateDonor | BusinessDonor>> {
    try {
      logger.info("activateDonations...", LoggingSource.UIDonor);

      logger.debug(
        `Parameters => Donor: ${JSON.stringify(donor)}`,
        LoggingSource.UIDonor
      );

      logger.timeStart("activateDonations call", LoggingSource.UIDonor);
      const result = await this._apiClient.patch<PrivateDonor | BusinessDonor>(
        `${this.URL}/${userReference}/activatedonations`,
        donor
      );
      logger.timeEnd("activateDonations call", LoggingSource.UIDonor);

      logger.debug(
        `activateDonations response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonor
      );
      return result;
    } catch (error) {
      logger.timeEnd("activateDonations call", LoggingSource.UIDonor);
      logger.error(JSON.stringify(error), LoggingSource.UIDonor);
      throw error;
    } finally {
      logger.info("...activateDonations", LoggingSource.UIDonor);
    }
  }

  // async pauseDonations(
  //   donor: PrivateDonor | BusinessDonor,
  //   existingEmail: string
  // ) {
  //   try {
  //     logger.info("pauseDonations...", LoggingSource.UIDonor);

  //     logger.debug(
  //       `Parameters => Donor: ${JSON.stringify(donor)}`,
  //       LoggingSource.UIDonor
  //     );

  //     logger.timeStart("pauseDonations call", LoggingSource.UIDonor);
  //     const result = await this._apiClient.patch<PrivateDonor | BusinessDonor>(
  //       `${this.URL}/${existingEmail}/pauseDonations`,
  //       donor
  //     );
  //     logger.timeEnd("pauseDonations call", LoggingSource.UIDonor);

  //     logger.debug(
  //       `pauseDonations response: ${JSON.stringify(result)}`,
  //       LoggingSource.UIDonor
  //     );
  //     return result;
  //   } catch (error) {
  //     logger.timeEnd("pauseDonations call", LoggingSource.UIDonor);
  //     logger.error(JSON.stringify(error), LoggingSource.UIDonor);
  //     throw error;
  //   } finally {
  //     logger.info("...pauseDonations", LoggingSource.UIDonor);
  //   }
  // }
  // async resumeDonations(
  //   donor: PrivateDonor | BusinessDonor,
  //   existingEmail: string
  // ) {
  //   try {
  //     logger.info("resumeDonations...", LoggingSource.UIDonor);

  //     logger.debug(
  //       `Parameters => Donor: ${JSON.stringify(donor)}`,
  //       LoggingSource.UIDonor
  //     );

  //     logger.timeStart("resumeDonations call", LoggingSource.UIDonor);
  //     const result = await this._apiClient.patch<PrivateDonor | BusinessDonor>(
  //       `${this.URL}/${existingEmail}/resumeDonations`,
  //       donor
  //     );
  //     logger.timeEnd("resumeDonations call", LoggingSource.UIDonor);

  //     logger.debug(
  //       `resumeDonations response: ${JSON.stringify(result)}`,
  //       LoggingSource.UIDonor
  //     );
  //     return result;
  //   } catch (error) {
  //     logger.timeEnd("resumeDonations call", LoggingSource.UIDonor);
  //     logger.error(JSON.stringify(error), LoggingSource.UIDonor);
  //     throw error;
  //   } finally {
  //     logger.info("...resumeDonations", LoggingSource.UIDonor);
  //   }
  // }
  // async deactiveDonations(
  //   donor: PrivateDonor | BusinessDonor,
  //   userReference: string
  // ): Promise<ResponseModel<PrivateDonor | BusinessDonor>> {
  //   try {
  //     logger.info("deactiveDonations...", LoggingSource.UIDonor);

  //     logger.debug(
  //       `Parameters => Donor: ${JSON.stringify(donor)}`,
  //       LoggingSource.UIDonor
  //     );

  //     logger.timeStart("deactiveDonations call", LoggingSource.UIDonor);
  //     const result = await this._apiClient.patch<PrivateDonor | BusinessDonor>(
  //       `${this.URL}/${userReference}/deactivateDonations`,
  //       donor
  //     );
  //     logger.timeEnd("deactiveDonations call", LoggingSource.UIDonor);

  //     logger.debug(
  //       `deactiveDonations response: ${JSON.stringify(result)}`,
  //       LoggingSource.UIDonor
  //     );
  //     return result;
  //   } catch (error) {
  //     logger.timeEnd("deactiveDonations call", LoggingSource.UIDonor);
  //     logger.error(JSON.stringify(error), LoggingSource.UIDonor);
  //     throw error;
  //   } finally {
  //     logger.info("...deactiveDonations", LoggingSource.UIDonor);
  //   }
  // }

  async updateDonationPayment(
    donor: PrivateDonor | BusinessDonor,
    userReference: string
  ): Promise<IResponseModel<PrivateDonor | BusinessDonor>> {
    try {
      logger.info("updateDonationPayment...", LoggingSource.UIDonor);

      logger.debug(
        `Parameters => Donor: ${JSON.stringify(donor)}`,
        LoggingSource.UIDonor
      );

      logger.timeStart("updateDonationPayment call", LoggingSource.UIDonor);
      const result = await this._apiClient.patch<PrivateDonor | BusinessDonor>(
        `${this.URL}/${userReference}/updateDonationPayment`,
        donor
      );
      logger.timeEnd("updateDonationPayment call", LoggingSource.UIDonor);

      logger.debug(
        `updateDonationPayment response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonor
      );
      return result;
    } catch (error) {
      logger.timeEnd("updateDonationPayment call", LoggingSource.UIDonor);
      logger.error(JSON.stringify(error), LoggingSource.UIDonor);
      throw error;
    } finally {
      logger.info("...updateDonationPayment", LoggingSource.UIDonor);
    }
  }

  async updateDonationSchedule(
    donor: PrivateDonor | BusinessDonor,
    userReference: string
  ): Promise<IResponseModel<PrivateDonor | BusinessDonor>> {
    try {
      logger.info("updateDonationSchedule...", LoggingSource.UIDonor);

      logger.debug(
        `Parameters => Donor: ${JSON.stringify(donor)}`,
        LoggingSource.UIDonor
      );

      logger.timeStart("updateDonationSchedule call", LoggingSource.UIDonor);
      const result = await this._apiClient.patch<PrivateDonor | BusinessDonor>(
        `${this.URL}/${userReference}/updateDonationSchedule`,
        donor
      );
      logger.timeEnd("updateDonationSchedule call", LoggingSource.UIDonor);

      logger.debug(
        `updateDonationSchedule response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonor
      );
      return result;
    } catch (error) {
      logger.timeEnd("updateDonationSchedule call", LoggingSource.UIDonor);
      logger.error(JSON.stringify(error), LoggingSource.UIDonor);
      throw error;
    } finally {
      logger.info("...updateDonationSchedule", LoggingSource.UIDonor);
    }
  }

  async updateDonationAccount(
    donor: PrivateDonor | BusinessDonor,
    userReference: string
  ): Promise<IResponseModel<PrivateDonor | BusinessDonor>> {
    try {
      logger.info("updateDonationAccount...", LoggingSource.UIDonor);

      logger.debug(
        `Parameters => Donor: ${JSON.stringify(donor)}`,
        LoggingSource.UIDonor
      );

      logger.timeStart("updateDonationAccount call", LoggingSource.UIDonor);
      const result = await this._apiClient.patch<PrivateDonor | BusinessDonor>(
        `${this.URL}/${userReference}/updateDonationAccount`,
        donor
      );
      logger.timeEnd("updateDonationAccount call", LoggingSource.UIDonor);

      logger.debug(
        `updateDonationAccount response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonor
      );
      return result;
    } catch (error) {
      logger.timeEnd("updateDonationAccount call", LoggingSource.UIDonor);
      logger.error(JSON.stringify(error), LoggingSource.UIDonor);
      throw error;
    } finally {
      logger.info("...updateDonationAccount", LoggingSource.UIDonor);
    }
  }

  async getByReference(
    userReference: string
  ): Promise<IResponseModel<PrivateDonor | BusinessDonor>> {
    try {
      logger.info("getByReference...", LoggingSource.UIDonor);

      logger.debug(
        `Parameters => UserReference: ${userReference}`,
        LoggingSource.UIDonor
      );

      logger.timeStart("getByReference call", LoggingSource.UIDonor);
      const result = await this._apiClient.get<PrivateDonor | BusinessDonor>(
        this.URL + `/${userReference}`
      );
      logger.timeEnd("getByReference call", LoggingSource.UIDonor);
      logger.debug(
        `getByReference Response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonor
      );
      return result;
    } catch (error) {
      logger.timeEnd("getByReference call", LoggingSource.UIDonor);
      logger.error(JSON.stringify(error), LoggingSource.UIDonor);
      throw error;
    } finally {
      logger.info("...getByReference", LoggingSource.UIDonor);
    }
  }
}
